import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'
import { LINKS } from './links'

/**
 * Ahmet Bilgen's contact page.
 *
 * Asked for 2026-09-15 at `/en/about/team/ahmet-bilgen`, which is where the
 * request named it, so that address is the slug. The page is a contact card
 * and nothing more: the name, the title, and the three channels he gave —
 * email and one number per country. No biography is invented here; what the
 * site says about the three co-CEOs stays on About Us, which this page links
 * back to.
 *
 * The details are the `features` grid rather than prose because each one is a
 * thing to act on: the value is the link (`mailto:`/`tel:`) and the label sits
 * under it, so a phone dials from the page instead of asking the reader to
 * copy a number out of a sentence. `localizeHref` passes both schemes through
 * untouched.
 *
 * Asked for 2026-09-15: the team contact pages should read as one set, so this
 * page's shape is the shared one — role in the eyebrow, one line of role under
 * a centred name, a `features` grid whose card title *is* the address or the
 * number, and the same closing band back to the firm.
 */
export const route: RouteDef = {
  id: 'teamAhmetBilgen',
  path: 'about/team/ahmet-bilgen',
  // Asked for 2026-09-15, same as the other two co-CEO cards: the page is
  // handed out as a QR code and reached by that link alone, so it stays out of
  // the sitemap and out of search results. The URL itself still works.
  robots: 'noindex',
  priority: 0.5,
}

const EMAIL = 'ahmet@digitopia.ai'
const LINKEDIN = 'https://www.linkedin.com/in/abilgen/'
// Asked for 2026-09-17, for every team page: a button beside LinkedIn that
// saves the details straight into the reader's phone. The file is a static
// vCard in `public/vcf/`, named after this page's slug — see that folder's
// README.
const VCARD = '/vcf/ahmet-bilgen.vcf'

export const teamAhmetBilgen: WrittenPage = {
  navLabel: 'Ahmet Bilgen',
  title: 'Ahmet Bilgen: Co-CEO, Technology',
  description:
    'Contact Ahmet Bilgen, Co-CEO of Digitopia for Technology: email ahmet@digitopia.ai, or call +1 650 213 7440 (US) or +90 532 307 85 95 (Türkiye).',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Co-CEO, Technology',
      headline: 'Ahmet Bilgen',
      align: 'center',
      // No portrait, asked for 2026-09-15: this page is scanned from a QR code
      // on a card that already has his face on it, so it is a set of details
      // rather than a profile.
      ctas: [
        { label: 'Email', href: `mailto:${EMAIL}` },
        { label: 'LinkedIn', href: LINKEDIN },
        { label: 'Save contact', href: VCARD },
      ],
    },
    {
      kind: 'features',
      heading: 'Get in touch',
      columns: 3,
      items: [
        {
          title: EMAIL,
          note: 'Email',
          href: `mailto:${EMAIL}`,
        },
        {
          title: '+1 650 213 7440',
          note: 'United States',
          href: 'tel:+16502137440',
        },
        {
          title: '+90 532 307 85 95',
          note: 'Türkiye',
          href: 'tel:+905323078595',
        },
      ],
      footnote: 'Digitopia works from New York, London and Istanbul.',
    },
    {
      kind: 'cta',
      heading: 'Rather start with the firm than a person?',
      sub: 'The contact form reaches the whole team, and About Us says who else you would be working with.',
      tone: 'band',
      ctas: [
        { label: 'Contact Digitopia', href: LINKS.contact },
        { label: 'About Digitopia', href: LINKS.about },
      ],
    },
  ],
}
